<template>
  <div class="download-buttons">
    <!-- 桌面端显示 -->
    <a
      v-if="isWindows"
      :href="windowsDownloadLink"
      class="download-button desktop-button windows"
      @click="handleDownloadClick('windows')"
    >
      <div class="button-icon">
        <img
          class="icon windows"
          src="@/assets/images/desktop-app/icon-pc.svg"
          alt="Windows"
        />
      </div>
      <div class="button-text">
        <span class="button-title">{{ $t('pikpak-pc-client') }}</span>
      </div>
    </a>
    <div
      v-else
      class="mac-container"
    >
      <a
        :href="macDownloadLink"
        class="download-button desktop-button mac"
        @click="handleDownloadClick('mac')"
      >
        <div class="button-icon">
          <img
            src="@/assets/images/desktop-app/icon-mac.svg"
            alt="Mac"
          />
        </div>
        <div class="button-text">
          <span class="button-title">{{ $t('pikpak-mac-client') }}</span>
          <span class="button-subtitle">
            {{ $t('mac-arch', { chipType: isAppleSilicon ? 'Apple Silicon' : 'Intel' }) }}
          </span>
        </div>
        <div
          class="another-arch"
          @click="showAnotherArchDownloadLinkVisible"
        />
      </a>

      <div>
        <a
          v-show="anotherArchDownloadLinkVisible"
          ref="anotherDownloadLink"
          :href="!isAppleSilicon ? APPLE_SILICON_DOWNLOAD_LINK : INTEL_DOWNLOAD_LINK"
          class="another-download-link"
          @click="handleDownloadClick(!isAppleSilicon ? 'mac_apple_silicon' : 'mac_intel')"
        >
          {{ $t('download-mac-arch', { chipType: !isAppleSilicon ? 'Apple Silicon' : 'Intel' }) }}
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from '#imports'
import { appInstallLink, getIsAppleSilicon, osNameInServerAndClient } from '@/composables/useInstallChannels'
import $track from '@/track/webShare'
import { APPLE_SILICON_DOWNLOAD_LINK, INTEL_DOWNLOAD_LINK } from '@/types/constants'
import { AppInstallPlatform, OSName } from '@/types/enum'
import { isWindows as checkIsWindows } from '@/utils'
import { onClickOutside } from '@vueuse/core'

interface DownloadButtonsEmits {
  (e: 'download', platform: string): void
}

const emit = defineEmits<DownloadButtonsEmits>()

const isAppleSilicon = ref(true)

// Mac 不同芯片架构下载按钮逻辑
const anotherArchDownloadLinkVisible = ref(false)
const anotherDownloadLink = ref<HTMLAnchorElement>()

const showAnotherArchDownloadLinkVisible = (evt: MouseEvent) => {
  evt.preventDefault()
  anotherArchDownloadLinkVisible.value = true
}

// 点击下载链接外部的区域
onClickOutside(anotherDownloadLink, () => {
  if (!anotherArchDownloadLinkVisible.value) return
  anotherArchDownloadLinkVisible.value = false
})

const osName = computed(() => osNameInServerAndClient())
const isWindows = computed(() => checkIsWindows())
const isMac = computed(() => osName.value === OSName.MAC_OS)

// 下载链接
const windowsDownloadLink = computed(() => appInstallLink(AppInstallPlatform.WINDOWS))

const macDownloadLink = computed(() => (isAppleSilicon.value ? APPLE_SILICON_DOWNLOAD_LINK : INTEL_DOWNLOAD_LINK))

// 下载点击处理
const handleDownloadClick = (platform: string) => {
  console.log(`Download clicked for platform: ${platform}`)

  // 桌面端埋点上报
  let button: Parameters<typeof $track.web_share_page_download_guide_click>[0]['button']
  switch (platform) {
    case 'windows':
      button = 'download_pc'
      break
    case 'mac':
      button = 'download_mac'
      break
    default:
      button = 'download_pc'
      break
  }

  $track.web_share_page_download_guide_click({ button })

  emit('download', platform)
}

// 初始化Apple Silicon检测
const initAppleSilicon = async () => {
  if (isMac.value) {
    isAppleSilicon.value = await getIsAppleSilicon()
  }
}

onMounted(() => {
  initAppleSilicon()
})
</script>

<style lang="less" scoped>
.download-buttons {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;

  .mobile-buttons-group {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .mac-container {
    position: relative;

    .another-download-link {
      position: absolute;
      width: calc(100% + 28px);
      height: 42px;
      top: calc(100% + 8px);
      left: 0;
      padding: 12px 18px;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 10px 0 rgba(13, 37, 83, 0.1);
      font-size: 17px;
      font-weight: 700;
      color: var(--color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      z-index: 100;

      &:hover {
        background-color: #fff;
        color: var(--color-primary-hover);
      }
    }
  }

  .download-button {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s ease;
    min-height: 44px;
    box-sizing: border-box;

    &.desktop-button {
      background: var(--color-primary);
      color: #fff;
      border: none;

      &.windows {
        background: var(--color-primary);

        &:hover {
          opacity: 0.9;
        }
      }

      &.mac {
        padding: 6px 12px;
        background: #000;
        position: relative;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        &:hover {
          background-color: #3c3c3c;
        }

        .another-arch {
          position: absolute;
          display: block;
          height: 100%;
          width: 28px;
          right: -28px;
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          background: url('@/assets/images/desktop-app/down-triangle.svg') rgba(0, 0, 0, 0.8) var(--no-repeat-center);
          cursor: pointer;
        }
      }

      &.generic {
        background: var(--color-primary);
      }
    }

    .button-icon {
      width: 28px;
      height: 28px;
      margin-right: 12px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .button-text {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;

      .button-title {
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #fff;
      }

      .button-subtitle {
        font-size: 13px;
        font-weight: 400;
        line-height: 18px;
        color: #fff;
        margin-top: 2px;
      }
    }
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;

    .download-button {
      width: 100%;
      max-width: 280px;
      justify-content: flex-start;

      .button-text .button-title {
        font-size: 13px;
      }
    }
  }
}
</style>
